import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    computed,
    effect,
    inject,
    Injector,
    input,
    NgZone,
    signal,
    viewChild,
    ViewContainerRef,
} from "@angular/core";
import { DashLangService } from "../../../../modules/home/dash/service/dash.lang.service";
import { DashPanelToolboxBridge } from "../../../../modules/home/dash/service/dash.panel.toolbox.bridge";
import { DashPanelService } from "../../../../modules/home/dash/service/dash.panel.service";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { UserprefService } from "../../../../services/userpref.service";
import { EventService } from "../../../../services/event.service";
import { LoginStore } from "../../../../stores/login.store";
import { s25Resource } from "../../../../util/s25.resource";
import { S25IconButtonComponent } from "../../../s25.icon.button.component";
import { HomeDashPanelHeadToggleComponent } from "./head/home.dash.panel.head.toggle.component";
import {
    HomeDashCustomize,
    HomeDashPanelModel,
    HomeDashPanelTypeId,
    HOME_DASH_NON_LIST_PANEL_TYPE_IDS,
} from "./home.dash.panel.model";
import { HOME_DASH_PANEL_HEAD_CHROME_INITIAL, HomeDashPanelHeadChrome } from "./head/home.dash.panel.head.state";
import {
    HomeDashPanelBodyContext,
    HomeDashPanelBodyDefinition,
    resolveHomeDashPanelBody,
} from "./home.dash.panel.body.registry";

/** Panel kinds whose body may bind {@link HomeDashPanelHeadChrome} to the orchestrator head. */
const PANEL_KINDS_WITH_HEAD_CHROME = new Set(["available_space"]);

type DashLangPanelRoot = {
    type: (typeId: number) => string;
    quick_search?: { style?: { panel?: string } };
    available_space?: { style?: { panel?: string } };
    create_event?: { style?: { panel?: string } };
    list?: { style?: Record<number, { panel?: string }> };
};

type OutputLike = { subscribe: (handler: (value: unknown) => void) => { unsubscribe: () => void } };

@TypeManagerDecorator("s25-ng-dash-panel")
@Component({
    selector: "s25-ng-dash-panel",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, HomeDashPanelHeadToggleComponent, S25IconButtonComponent],
    host: { class: "s25-ng-dash-panel" },
    template: `
        @if (panelKind()) {
            <div
                [attr.id]="panelShellId() ?? null"
                class="c-dashPanelShell c-sectionHead"
                [ngClass]="headIconNgClass()"
                [class.sectionHead--collapsed]="panel().isCollapsed"
            >
                @if (headReady() && showHeader()) {
                    <div
                        class="d-head sectionHead c-sectionHead"
                        [ngClass]="headIconNgClass()"
                        [class.sectionHead--collapsed]="panel().isCollapsed"
                    >
                        <div class="dash-tb-body c-dashPanelRow">
                            <span
                                class="tb-col dh-col-b c-dash-panel--header"
                                [attr.tabindex]="customize()?.toolboxOpen() ? '0' : '-1'"
                                role="heading"
                                aria-level="2"
                                [attr.aria-label]="ariaHeading()"
                                [attr.title]="headerTooltipContent() ?? null"
                                [class.sortable-handle]="isSortable()"
                                (focus)="onHeaderFocus($event)"
                                (blur)="onHeaderBlur($event)"
                            >
                                {{ headerText() }}
                            </span>
                            @if (showCollapseToggle() || showHeadBackColumn()) {
                                <div class="c-dashPanelHeadActions">
                                    @if (showHeadBackColumn()) {
                                        <s25-ng-icon-button
                                            class="c-headToggle c-dashPanelHeadBack"
                                            data-dash-panel-head-back
                                            type="arrowLeft"
                                            [ariaLabel]="headBackTooltipText()"
                                            [size]="'1.25em'"
                                            [attr.title]="headBackTooltipText()"
                                            (clicked)="onHeadBackActivate()"
                                        />
                                    }
                                    @if (showCollapseToggle()) {
                                        <s25-ng-dash-panel-head-toggle
                                            [collapsed]="!!panel().isCollapsed"
                                            [headerText]="headerText()"
                                            [collapseTooltip]="collapseToggleTooltip()"
                                            [expandTooltip]="expandToggleTooltip()"
                                            (activate)="onHeadToggleActivate()"
                                        />
                                    }
                                </div>
                            }
                        </div>
                    </div>
                }
                <ng-container #bodyHost></ng-container>
            </div>
        }
    `,
})
export class HomeDashPanelComponent {
    readonly panel = input.required<HomeDashPanelModel>();
    readonly isToolbox = input(false);
    readonly customize = input<HomeDashCustomize | undefined>(undefined);
    /** When false, the section head (title, collapse toggle host) is omitted — e.g. reserve-only Create Event ({@link HomeDashPanelTypeId.ExpressSchedulingReserveOnly}) on dash. */
    readonly showHeader = input(true);

    private readonly ngZone = inject(NgZone);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly hostInjector = inject(Injector);
    private readonly loginStore = inject(LoginStore);

    private readonly bodyHost = viewChild("bodyHost", { read: ViewContainerRef });
    private readonly bodyComponentRef = signal<ComponentRef<unknown> | undefined>(undefined);
    private readonly bodyDefinition = signal<HomeDashPanelBodyDefinition | undefined>(undefined);

    readonly dashLang = s25Resource(() => DashLangService.getLang());
    readonly listRefreshTick = signal(0);

    /** Chrome reported by the active panel body (e.g. wizard back control). */
    readonly panelHeadChrome = signal<HomeDashPanelHeadChrome>(HOME_DASH_PANEL_HEAD_CHROME_INITIAL);
    /** Bumped when the user activates the head back control; panel bodies reset on change. */
    readonly headBackTick = signal(0);

    readonly expressPermsForHead = s25Resource({
        if: () =>
            this.panel().typeId === HomeDashPanelTypeId.ExpressScheduling && this.panelKind() === "express_schedule",
        loader: () => EventService.hasExpressPerms(),
    });

    private readonly langHead = signal<Record<string, unknown> | undefined>(undefined);
    readonly headReady = signal(false);
    /** Web user-type only; login comes from {@link LoginStore}. */
    private readonly userTypeWeb = signal<boolean | undefined>(undefined);

    /**
     * {@link CompileService.compileElement} sets Angular `setInput` once from the AngularJS `data-model` object.
     * In-place mutations on that shared panel do not re-trigger `setInput`, so child inputs can stay stale. Pass a
     * shallow copy whenever {@link listRefreshTick} bumps so list / quick-search / available-space see loading/items.
     */
    readonly dashPanelViewModelForChild = computed((): HomeDashPanelModel => {
        this.listRefreshTick();
        return { ...this.panel() };
    });

    readonly isSortable = computed(() => this.loginStore.loggedIn());

    readonly panelKind = computed(() => {
        const langVal = this.dashLang.value();
        const panelModel = this.panel();
        if (!langVal) {
            return undefined;
        }
        return langVal.panel.type(panelModel.typeId as number);
    });

    readonly panelShellId = computed(() => {
        const langVal = this.dashLang.value();
        const panelModel = this.panel();
        const panelBlock = langVal?.panel as DashLangPanelRoot | undefined;
        if (!panelBlock) {
            return undefined;
        }
        const kind = panelBlock.type(panelModel.typeId as number);
        switch (kind) {
            case "quick_search":
                return panelBlock.quick_search?.style?.panel;
            case "available_space":
                return panelBlock.available_space?.style?.panel;
            case "express_schedule":
            case "open_event_form":
                return panelBlock.create_event?.style?.panel;
            default: {
                const listStyle = panelBlock.list?.style?.[panelModel.typeId as number];
                return listStyle?.panel;
            }
        }
    });

    readonly expressSchedulingActiveForHead = computed(() => {
        if (this.panelKind() !== "express_schedule") {
            return false;
        }
        return this.expressPermsForHead.value() === true;
    });

    readonly iconClassA = computed(() => {
        const head = this.langHead()?.["col"] as Record<string, unknown> | undefined;
        const iconFn = head?.["a"] as { icon?: (typeId: number, loggedIn: boolean) => string } | undefined;
        const panelRef = this.panel();
        return iconFn?.icon?.(panelRef.typeId as number, this.loginStore.loggedIn());
    });

    readonly headerText = computed(() => {
        const head = this.langHead()?.["col"] as Record<string, unknown> | undefined;
        const textFn = head?.["b"] as
            | {
                  text?: (
                      typeId: number,
                      loggedIn: boolean,
                      userTypeWeb: boolean,
                      expressSchedulingActive?: boolean,
                  ) => string;
              }
            | undefined;
        const panelRef = this.panel();
        return (
            textFn?.text?.(
                panelRef.typeId as number,
                this.loginStore.loggedIn(),
                !!this.userTypeWeb(),
                this.expressSchedulingActiveForHead(),
            ) ?? ""
        );
    });

    readonly headIconNgClass = computed((): Record<string, boolean> => {
        const icon = this.iconClassA();
        return icon ? { [`c-sectionHead--${icon}`]: true } : {};
    });

    readonly showHeadBackColumn = computed(() => !this.isToolbox() && !!this.panelHeadChrome().showBack);

    readonly headBackTooltipText = computed(() => {
        const fromPanel = this.panelHeadChrome().backTooltip;
        if (fromPanel) {
            return fromPanel;
        }
        const head = this.langHead()?.["col"] as Record<string, unknown> | undefined;
        const tooltipFn = head?.["c"] as { tooltip?: (typeId: number) => string | undefined } | undefined;
        const panelRef = this.panel();
        return tooltipFn?.tooltip?.(panelRef.typeId as number) ?? "";
    });

    readonly showCollapseToggle = computed(() => this.showHeader() && !this.isToolbox());

    readonly ariaHeading = computed(() => {
        const text = this.headerText();
        const active = this.showCollapseToggle();
        const collapsed = !!this.panel().isCollapsed;
        if (!active) {
            return text;
        }
        return `${text}${collapsed ? ", Collapsed" : ", Expanded"}`;
    });

    readonly headerTooltipContent = computed((): string | undefined => {
        if (this.isToolbox()) {
            return undefined;
        }
        const panelRef = this.panel();
        const head = this.langHead()?.["tthelp"] as Record<number, { content?: unknown }> | undefined;
        const entry = head?.[panelRef.typeId as number];
        const content = entry?.content;
        return typeof content === "string" ? content : undefined;
    });

    readonly collapseToggleTooltip = computed(() => {
        const head = this.langHead()?.["col"] as Record<string, unknown> | undefined;
        const tooltipColD = head?.["d"] as { tooltip?: (collapsed: boolean) => string } | undefined;
        return tooltipColD?.tooltip?.(false) ?? "";
    });

    readonly expandToggleTooltip = computed(() => {
        const head = this.langHead()?.["col"] as Record<string, unknown> | undefined;
        const tooltipColD = head?.["d"] as { tooltip?: (collapsed: boolean) => string } | undefined;
        return tooltipColD?.tooltip?.(true) ?? "";
    });

    constructor() {
        void this.bootstrapHeadLang();

        effect((onCleanup) => {
            const langVal = this.dashLang.value();
            const panelModel = this.panel();
            if (!langVal) {
                return;
            }
            const unregister = DashPanelToolboxBridge.register(panelModel.panelId, () => {
                this.ngZone.run(() => {
                    this.handleFromToolbox(panelModel);
                });
            });
            onCleanup(unregister);
        });

        effect(() => {
            const kind = this.panelKind();
            if (!kind || PANEL_KINDS_WITH_HEAD_CHROME.has(kind)) {
                return;
            }
            this.panelHeadChrome.set(HOME_DASH_PANEL_HEAD_CHROME_INITIAL);
        });

        effect((onCleanup) => {
            const host = this.bodyHost();
            const kind = this.panelKind();
            if (!host || !kind) {
                this.bodyComponentRef.set(undefined);
                this.bodyDefinition.set(undefined);
                return;
            }

            const definition = resolveHomeDashPanelBody(kind);
            host.clear();
            const componentRef = host.createComponent(definition.component, { injector: this.hostInjector });
            this.bodyDefinition.set(definition);
            this.bodyComponentRef.set(componentRef);

            const outputUnsubscribes = this.bindBodyOutputs(componentRef, definition);

            onCleanup(() => {
                for (const unsubscribe of outputUnsubscribes) {
                    unsubscribe();
                }
                this.bodyComponentRef.set(undefined);
                this.bodyDefinition.set(undefined);
            });
        });

        effect(() => {
            const componentRef = this.bodyComponentRef();
            const definition = this.bodyDefinition();
            if (!componentRef || !definition) {
                return;
            }
            this.applyBodyInputs(componentRef, definition, this.buildBodyContext());
        });

        afterNextRender(() => {
            queueMicrotask(() => this.runLoadAsync());
        });
    }

    onHeadToggleActivate(): void {
        const panelModel = this.panel();
        panelModel.isCollapsed = !panelModel.isCollapsed;
        this.toggle();
    }

    toggle(): void {
        this.bumpListRefresh();
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        const panelModel = this.panel();
        if (panelModel.isInitiallyEmpty && !panelModel.isCollapsed) {
            void this.putThenRefresh(panelModel);
        } else {
            void this.put(panelModel);
        }
    }

    onHeaderFocus(event: FocusEvent): void {
        if (!this.isSortable()) {
            return;
        }
        this.customize()?.onFocus?.(event);
    }

    onHeaderBlur(event: FocusEvent): void {
        if (!this.isSortable()) {
            return;
        }
        this.customize()?.onFocusOut?.(event);
    }

    onHeadBackActivate(): void {
        this.ensurePanelExpandedForHeadAction();
        this.headBackTick.update((tick) => tick + 1);
    }

    private buildBodyContext(): HomeDashPanelBodyContext {
        return {
            panel: this.panel(),
            viewModel: this.dashPanelViewModelForChild(),
            isToolbox: this.isToolbox(),
            customize: this.customize(),
            refreshTick: this.listRefreshTick(),
            headBackTick: this.headBackTick(),
            isSortable: this.isSortable(),
        };
    }

    private applyBodyInputs(
        componentRef: ComponentRef<unknown>,
        definition: HomeDashPanelBodyDefinition,
        context: HomeDashPanelBodyContext,
    ): void {
        for (const [inputName, inputValue] of Object.entries(definition.inputs(context))) {
            componentRef.setInput(inputName, inputValue);
        }
    }

    private bindBodyOutputs(
        componentRef: ComponentRef<unknown>,
        definition: HomeDashPanelBodyDefinition,
    ): Array<() => void> {
        const outputHandlers = definition.outputs?.({
            onHeadChromeChange: (chrome) => this.panelHeadChrome.set(chrome),
        });
        if (!outputHandlers) {
            return [];
        }
        const unsubscribes: Array<() => void> = [];
        const instance = componentRef.instance as Record<string, OutputLike | undefined>;
        for (const [outputName, handler] of Object.entries(outputHandlers)) {
            const outputRef = instance[outputName];
            const subscription = outputRef?.subscribe(handler);
            if (subscription) {
                unsubscribes.push(() => subscription.unsubscribe());
            }
        }
        return unsubscribes;
    }

    private ensurePanelExpandedForHeadAction(): void {
        const panelRef = this.panel();
        if (panelRef.isCollapsed) {
            panelRef.isInitiallyEmpty = undefined;
            panelRef.isCollapsed = false;
            this.toggle();
        }
    }

    private async bootstrapHeadLang(): Promise<void> {
        const [lang, userTypeWeb] = await Promise.all([DashLangService.getLang(), UserprefService.getUserTypeWeb()]);
        const head = lang.panel?.["head"] as Record<string, unknown>;
        this.langHead.set(head);
        this.userTypeWeb.set(!!userTypeWeb);
        this.ngZone.run(() => {
            this.headReady.set(true);
        });
    }

    private runLoadAsync(): void {
        const panelModel = this.panel();
        if (!this.isToolbox() && panelModel.isAsync && panelModel.isInitiallyEmpty && !panelModel.isCollapsed) {
            void this.loadPanelData();
        }
    }

    private handleFromToolbox(panelModel: HomeDashPanelModel): void {
        if (panelModel.isCollapsed) {
            panelModel.isInitiallyEmpty = true;
            this.bumpListRefresh();
            return;
        }
        const kind = this.panelKind();
        const numericTypeId = panelModel.typeId as number;
        /** Favorites / tasks / etc. use `panel-items.json`; toolbox copies often omit `isInitiallyEmpty`. */
        const usesPanelItemsList =
            kind === "list" || (kind === undefined && !HOME_DASH_NON_LIST_PANEL_TYPE_IDS.has(numericTypeId));
        const shouldToolboxCollapseResync =
            usesPanelItemsList ||
            (panelModel.isAsync && panelModel.isInitiallyEmpty) ||
            kind === "quick_search" ||
            kind === "available_space";
        if (shouldToolboxCollapseResync) {
            /**
             * Toolbox→dash: load panel-items, then bump/CD across frames so list children
             * see data while {@link HomeDashPanelListComponent.showListBody} stays true — a collapse pulse hid the
             * list body, so items could not render until refresh tick caught up.
             */
            void this.afterToolboxDropFetchThenSyncChildViews(panelModel);
            return;
        }
        this.markPanelNotLoadingAndBumpUiInZone(panelModel);
    }

    private bumpListRefresh(): void {
        this.listRefreshTick.update((tick) => tick + 1);
    }

    private bumpListRefreshDetectChangesInZone(): void {
        this.ngZone.run(() => {
            this.bumpListRefresh();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        });
    }

    private markPanelNotLoadingAndBumpUiInZone(panelModel: HomeDashPanelModel): void {
        this.ngZone.run(() => {
            panelModel.isLoading = false;
            this.bumpListRefresh();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        });
    }

    private async afterToolboxDropFetchThenSyncChildViews(panelModel: HomeDashPanelModel): Promise<void> {
        if (this.isToolbox()) {
            return;
        }
        await this.fetchPanelItems(panelModel, "refresh");
        this.bumpListRefreshDetectChangesInZone();
        queueMicrotask(() => this.bumpListRefreshDetectChangesInZone());
        afterNextRender(() => this.bumpListRefreshDetectChangesInZone(), { injector: this.hostInjector });
    }

    private async digestPause(): Promise<void> {
        await new Promise<void>((resolve) => {
            this.ngZone.run(() => setTimeout(resolve, 0));
        });
    }

    private async setLoading(panelModel: HomeDashPanelModel): Promise<void> {
        this.ngZone.run(() => {
            Object.assign(panelModel, {
                items: undefined,
                isInitiallyEmpty: undefined,
                isLoading: true,
            });
            this.bumpListRefresh();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        });
        await this.digestPause();
    }

    private setReady(panelModel: HomeDashPanelModel): void {
        this.ngZone.run(() => {
            panelModel.isLoading = false;
            this.bumpListRefresh();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        });
    }

    private async loadPanelData(): Promise<void> {
        await this.fetchPanelItems(this.panel(), "get");
    }

    private async refreshPanelData(): Promise<void> {
        await this.fetchPanelItems(this.panel(), "refresh");
    }

    private async fetchPanelItems(panelModel: HomeDashPanelModel, mode: "get" | "refresh"): Promise<void> {
        try {
            await this.setLoading(panelModel);
            const items =
                mode === "get"
                    ? await DashPanelService.get(panelModel.panelId, panelModel.typeId)
                    : await DashPanelService.refresh(panelModel.panelId, panelModel.typeId);
            panelModel.items = items ?? undefined;
        } finally {
            this.setReady(panelModel);
        }
    }

    private async put(panelModel: HomeDashPanelModel): Promise<void> {
        if (!this.loginStore.loggedIn()) {
            return;
        }
        await DashPanelService.putIsCollapsed(panelModel.panelId, !!panelModel.isCollapsed);
    }

    private async putThenRefresh(panelModel: HomeDashPanelModel): Promise<void> {
        await this.setLoading(panelModel);
        await this.put(panelModel);
        await this.fetchPanelItems(panelModel, "refresh");
    }
}
